import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Edit,
  Key,
  Lock,
  Unlock,
  UserCheck,
  UserX,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { userService } from '@/services/admin/user.service';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EditUserDialog } from './components/EditUserDialog';
import { ResetPasswordDialog } from './components/ResetPasswordDialog';
import { UserActivityTab } from './components/UserActivityTab';
import { UserDetailsOverview } from './components/UserDetailsOverview';
import { UserPermissionsTab } from './components/UserPermissionsTab';
import { UserRolesTab } from './components/UserRolesTab';

export function UserDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isResetPasswordDialogOpen, setIsResetPasswordDialogOpen] =
    useState(false);
  const [lockAlertOpen, setLockAlertOpen] = useState(false);
  const [unlockAlertOpen, setUnlockAlertOpen] = useState(false);
  const [activateAlertOpen, setActivateAlertOpen] = useState(false);
  const [deactivateAlertOpen, setDeactivateAlertOpen] = useState(false);
  const isValidId =
    !!id &&
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
      id,
    );

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (
      hash &&
      ['overview', 'roles', 'permissions', 'activity'].includes(hash)
    ) {
      setActiveTab(hash);
    }
  }, []);

  const {
    data: user,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['users', id],
    queryFn: () => userService.getUser(id!),
    enabled: isValidId,
  });

  const lockMutation = useMutation({
    mutationFn: () => userService.lockUser(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', id] });
      toast.success('User account locked successfully');
      setLockAlertOpen(false);
    },
    onError: (error: Error) => {
      toast.error(`Failed to lock user: ${error.message}`);
    },
  });

  const unlockMutation = useMutation({
    mutationFn: () => userService.unlockUser(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', id] });
      toast.success('User account unlocked successfully');
      setUnlockAlertOpen(false);
    },
    onError: (error: Error) => {
      toast.error(`Failed to unlock user: ${error.message}`);
    },
  });

  const activateMutation = useMutation({
    mutationFn: () => userService.activateUser(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', id] });
      toast.success('User account activated successfully');
      setActivateAlertOpen(false);
    },
    onError: (error: Error) => {
      toast.error(`Failed to activate user: ${error.message}`);
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: () => userService.deactivateUser(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', id] });
      toast.success('User account deactivated successfully');
      setDeactivateAlertOpen(false);
    },
    onError: (error: Error) => {
      toast.error(`Failed to deactivate user: ${error.message}`);
    },
  });

  const isUserLocked =
    user?.lockoutEnd && new Date(user.lockoutEnd) > new Date();

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <Skeleton className="h-10 w-1/3" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!isValidId) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-destructive">
              Invalid user id. Please select a user from the list.
            </p>
            <Button
              variant="outline"
              onClick={() => navigate('/users')}
              className="mt-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Users
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-destructive">Error loading user details</p>
            <Button
              variant="outline"
              onClick={() => navigate('/users')}
              className="mt-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Users
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Breadcrumb and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/users')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <Link
                to="/users"
                className="text-sm text-muted-foreground hover:underline"
              >
                Users
              </Link>
              <span className="text-sm text-muted-foreground">/</span>
              <h1 className="text-2xl font-bold">
                {user.name || user.userName} {user.surname || ''}
              </h1>
            </div>
            <p className="text-sm text-muted-foreground">@{user.userName}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isUserLocked ? (
            <Button variant="outline" onClick={() => setUnlockAlertOpen(true)}>
              <Unlock className="h-4 w-4 mr-2" />
              Unlock Account
            </Button>
          ) : (
            <Button variant="outline" onClick={() => setLockAlertOpen(true)}>
              <Lock className="h-4 w-4 mr-2" />
              Lock Account
            </Button>
          )}

          {user.isActive ? (
            <Button
              variant="outline"
              onClick={() => setDeactivateAlertOpen(true)}
            >
              <UserX className="h-4 w-4 mr-2" />
              Deactivate
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => setActivateAlertOpen(true)}
            >
              <UserCheck className="h-4 w-4 mr-2" />
              Activate
            </Button>
          )}

          <Button
            variant="outline"
            onClick={() => setIsResetPasswordDialogOpen(true)}
          >
            <Key className="h-4 w-4 mr-2" />
            Reset Password
          </Button>

          <Button onClick={() => setIsEditDialogOpen(true)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit User
          </Button>
        </div>
      </div>

      {/* User Details Tabs */}
      <Card>
        <CardHeader className="pb-3">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="roles">Roles</TabsTrigger>
              <TabsTrigger value="permissions">Permissions</TabsTrigger>
              <TabsTrigger value="activity">Activity</TabsTrigger>
            </TabsList>

            <div className="mt-6">
              <TabsContent value="overview" className="m-0">
                <UserDetailsOverview user={user} />
              </TabsContent>

              <TabsContent value="roles" className="m-0">
                <UserRolesTab userId={user.id} />
              </TabsContent>

              <TabsContent value="permissions" className="m-0">
                <UserPermissionsTab userId={user.id} />
              </TabsContent>

              <TabsContent value="activity" className="m-0">
                <UserActivityTab userId={user.id} />
              </TabsContent>
            </div>
          </Tabs>
        </CardHeader>
      </Card>

      {/* Dialogs */}
      <EditUserDialog
        user={user}
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
      />

      <ResetPasswordDialog
        userId={user.id}
        userName={user.userName}
        open={isResetPasswordDialogOpen}
        onClose={() => setIsResetPasswordDialogOpen(false)}
      />

      {/* Lock Account Alert */}
      <AlertDialog open={lockAlertOpen} onOpenChange={setLockAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Lock User Account?</AlertDialogTitle>
            <AlertDialogDescription>
              This will prevent <strong>{user.userName}</strong> from logging in
              until the account is unlocked. This action can be reversed at any
              time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => lockMutation.mutate()}>
              Lock Account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unlock Account Alert */}
      <AlertDialog open={unlockAlertOpen} onOpenChange={setUnlockAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unlock User Account?</AlertDialogTitle>
            <AlertDialogDescription>
              This will allow <strong>{user.userName}</strong> to login again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => unlockMutation.mutate()}>
              Unlock Account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Activate Account Alert */}
      <AlertDialog open={activateAlertOpen} onOpenChange={setActivateAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Activate User Account?</AlertDialogTitle>
            <AlertDialogDescription>
              This will activate <strong>{user.userName}</strong>'s account and
              allow them to login.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => activateMutation.mutate()}>
              Activate Account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Deactivate Account Alert */}
      <AlertDialog
        open={deactivateAlertOpen}
        onOpenChange={setDeactivateAlertOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate User Account?</AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate <strong>{user.userName}</strong>'s account
              and prevent them from logging in. This action can be reversed at
              any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deactivateMutation.mutate()}>
              Deactivate Account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
